import { KeyboardLightingFeature } from '../../../../features/keyboard-lighting/contract'
import { LightingDomainRegistry } from '../../../../features/keyboard-lighting/LightingDomainRegistry'
import { staticDomain, dynamicDomain, sideDomain, rgbDomain } from './domains'
import { ADDRESS_TELINK } from '../../protocols/address'
import { sendRead, sendWrite, checkACK } from '../../capabilities/memory'
import * as shared from '../../../shared/feature-impls/keyboard-lighting/lighting'
import { LightingDomainType, type LightingConfigData } from '../../../../types/lightingDomain'
import type { KeyboardLightingTarget } from '../../../../types/lighting'
import type {
  keyboardLightingCustomConf,
  AuxLightingEnableRegister,
} from '../../../../types/keyboardLighting'
import type { keyMapLighting } from '../../../../types/keyboardLighting'
import type { CommonAddress } from '../../../../types/protocol'
import { TELINK_KEYBOARD_COMMAND } from '../../protocols/keyboard-command'

export class TelinkKeyboardLighting extends KeyboardLightingFeature {
  constructor(device: any) {
    super(device)
    LightingDomainRegistry.register(staticDomain)
    LightingDomainRegistry.register(dynamicDomain)
    LightingDomainRegistry.register(sideDomain)
    LightingDomainRegistry.register(rgbDomain)
  }

  getSupportedDomains() {
    return shared.getSupportedDomains()
  }

  async getEffects(target?: KeyboardLightingTarget) {
    return shared.getEffects(this.device, sendRead, target)
  }

  // ==================== 领域通用 CRUD ====================

  async getConfig(domain: LightingDomainType, index: number) {
    return shared.getConfig(this.device, sendRead, domain, index)
  }

  async setConfig(domain: LightingDomainType, index: number, config: Partial<LightingConfigData>) {
    return shared.setConfig(this.device, sendRead, sendWrite, domain, index, config)
  }

  async setProperty(
    domain: LightingDomainType,
    index: number,
    key: string,
    value: Parameters<typeof shared.setProperty>[6],
  ) {
    return shared.setProperty(this.device, sendRead, sendWrite, domain, index, key, value)
  }

  async getProperty(domain: LightingDomainType, index: number, key: string) {
    return shared.getProperty(this.device, sendRead, domain, index, key)
  }

  // ==================== 设备级操作（背光） ====================

  async setBacklightSwitch(enabled: boolean) {
    return shared.setBacklightSwitch(
      this.device,
      sendWrite,
      2000,
      ADDRESS_TELINK.BACKLIGHT_SWITCH,
      enabled,
    )
  }

  async getBacklightSwitch() {
    return shared.getBacklightSwitch(this.device, sendRead, ADDRESS_TELINK.BACKLIGHT_SWITCH)
  }

  async setCurrentEffect(effect: number) {
    return shared.setCurrentEffect(
      this.device,
      sendWrite,
      2000,
      ADDRESS_TELINK.BACKLIGHT_EFFECT,
      effect,
    )
  }

  async getCurrentEffect() {
    return shared.getCurrentEffect(this.device, sendRead, ADDRESS_TELINK.BACKLIGHT_EFFECT)
  }

  async setBrightness(brightness: number) {
    return shared.setBrightness(
      this.device,
      sendWrite,
      2000,
      ADDRESS_TELINK.GLOBAL_BRIGHTNESS,
      brightness,
    )
  }

  async getBrightness() {
    return shared.getBrightness(this.device, sendRead, ADDRESS_TELINK.GLOBAL_BRIGHTNESS)
  }

  async setSpeed(speed: number) {
    return shared.setSpeed(this.device, sendWrite, 2000, ADDRESS_TELINK.GLOBAL_SPEED, speed)
  }

  async getSpeed() {
    return shared.getSpeed(this.device, sendRead, ADDRESS_TELINK.GLOBAL_SPEED)
  }

  // ==================== 逐键 RGB ====================

  async setKeysColor(_domain: LightingDomainType, keyMap: keyMapLighting, index: number) {
    return shared.setKeysColor(
      this.device,
      sendWrite,
      2000,
      22,
      ADDRESS_TELINK.STATIC_CUSTOM_LIGHT_RGB as unknown as Record<
        number,
        Record<number, Record<number, CommonAddress>>
      >,
      ADDRESS_TELINK.STATIC_LIGHT_R0C0,
      keyMap,
      index,
    )
  }

  async getKeysColor(domain: LightingDomainType, index: number) {
    return shared.getKeysColor(
      this.device,
      sendRead,
      domain,
      index,
      22,
      6,
      ADDRESS_TELINK.STATIC_CUSTOM_LIGHT_RGB as unknown as Record<
        number,
        Record<number, Record<number, CommonAddress>>
      >,
    )
  }

  async *getKeysColorStream(domain: LightingDomainType, index: number) {
    yield* shared.getKeysColorStream(
      this.device,
      sendRead,
      domain,
      index,
      22,
      6,
      ADDRESS_TELINK.STATIC_CUSTOM_LIGHT_RGB as unknown as Record<
        number,
        Record<number, Record<number, CommonAddress>>
      >,
    )
  }

  // ==================== 自定义灯效 ====================

  async createCustomEffect(config: keyboardLightingCustomConf, index: number) {
    return shared.createCustomEffect(
      this.device,
      sendWrite,
      0x14,
      0x0c,
      ADDRESS_TELINK.STATIC_LIGHT_EFFECT_ADDRESS_NAME_LEVEL,
      ADDRESS_TELINK.STATIC_LIGHT_TYPE,
      config,
      index,
    )
  }

  async deleteCustomEffect(index: number) {
    return shared.deleteCustomEffect(
      this.device,
      sendRead,
      sendWrite,
      checkACK,
      2000,
      ADDRESS_TELINK.BACKLIGHT_EFFECT,
      [...TELINK_KEYBOARD_COMMAND.delete_Lighting],
      0xba,
      0x08,
      index,
    )
  }

  // ==================== 侧灯操作 ====================
  // 通过使能寄存器 SIDE_LIGHT_ENABLE (+0x84) 的 byte2/byte3 控制侧灯模式/开关。
  // 使用 shared 的 getSideLightEnableBytes/setSideLightEnableBytes 读写原始字节，
  // 配合 serializeAuxLightingEnableRegister 构建 AuxLightingEnableRegister 结构。

  async setSideEffect(effect: number) {
    const bytes = await shared.getSideLightEnableBytes(
      this.device,
      sendRead,
      ADDRESS_TELINK.SIDE_LIGHT_ENABLE,
    )
    const reg = shared.parseAuxLightingEnableBytes(bytes)
    reg.sideLightMode = effect
    await shared.setSideLightEnableBytes(
      this.device,
      sendWrite,
      2000,
      ADDRESS_TELINK.SIDE_LIGHT_ENABLE,
      Array.from(shared.serializeAuxLightingEnableRegister(reg)),
    )
  }

  async getSideEffect(): Promise<number> {
    const bytes = await shared.getSideLightEnableBytes(
      this.device,
      sendRead,
      ADDRESS_TELINK.SIDE_LIGHT_ENABLE,
    )
    return shared.parseAuxLightingEnableBytes(bytes).sideLightMode
  }

  async setSideLightDynamicEnable(enable: boolean) {
    const bytes = await shared.getSideLightEnableBytes(
      this.device,
      sendRead,
      ADDRESS_TELINK.SIDE_LIGHT_ENABLE,
    )
    const reg = shared.parseAuxLightingEnableBytes(bytes)
    reg.sideLightEnabled = enable
    await shared.setSideLightEnableBytes(
      this.device,
      sendWrite,
      2000,
      ADDRESS_TELINK.SIDE_LIGHT_ENABLE,
      Array.from(shared.serializeAuxLightingEnableRegister(reg)),
    )
  }

  async getSideLightDynamicEnable(): Promise<boolean> {
    const bytes = await shared.getSideLightEnableBytes(
      this.device,
      sendRead,
      ADDRESS_TELINK.SIDE_LIGHT_ENABLE,
    )
    return shared.parseAuxLightingEnableBytes(bytes).sideLightEnabled
  }

  // ==================== 辅助灯区使能寄存器语义化操作 ====================

  async getAuxLightingEnableRegister(): Promise<AuxLightingEnableRegister> {
    const bytes = await shared.getSideLightEnableBytes(
      this.device,
      sendRead,
      ADDRESS_TELINK.SIDE_LIGHT_ENABLE,
    )
    return shared.parseAuxLightingEnableBytes(bytes)
  }

  async setAuxLightingEnableRegister(reg: Partial<AuxLightingEnableRegister>): Promise<void> {
    const bytes = await shared.getSideLightEnableBytes(
      this.device,
      sendRead,
      ADDRESS_TELINK.SIDE_LIGHT_ENABLE,
    )
    const current = shared.parseAuxLightingEnableBytes(bytes)
    const merged = { ...current, ...reg }
    await shared.setSideLightEnableBytes(
      this.device,
      sendWrite,
      2000,
      ADDRESS_TELINK.SIDE_LIGHT_ENABLE,
      Array.from(shared.serializeAuxLightingEnableRegister(merged)),
    )
  }
}
